import React, { useState } from "react";
import { Breadcrumb, Button, Card, Col, Divider, Modal, Row, Table, TableColumnProps, Tag, Upload, message, Input, Select, DatePicker } from "antd";
import { UploadOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import usePayments from "./usePayments";
import Permission from "../../components/general/permission/permission";
import { PAYMENT_STATUS, PAYMENT_STATUS_COLOR } from "../../constants/constants";
import { IPayment } from "../../types/interfaces";
import { uploadPaymentReceipt } from "../../services/payment";
import { files, downloadFile } from "../../services/files";

const { confirm } = Modal;

const Payments: React.FC = () => {
  const {
    data,
    loading,
    total,
    tableParams,
    onTableChange,
    handleApprove,
    handleReject,
    isEmpresa,
    refetch,
    setSearchTerm,
    setStatusFilter,
    setDateRange,
  } = usePayments();

  const [uploadingId, setUploadingId] = useState<number | null>(null);
  const [downloadingUrl, setDownloadingUrl] = useState<{[key: string]: string}>({});

  const handleUploadReceipt = async (file: File, paymentId: number) => {
    try {
      setUploadingId(paymentId);
      message.loading({ content: "Subiendo comprobante...", key: "uploading" });
      const uploadedFile = await files({ file: file as any, category: "receipts" });
      if (uploadedFile) await uploadPaymentReceipt(paymentId, uploadedFile.path);
      message.success({ content: "Comprobante subido exitosamente", key: "uploading" });
      refetch();
    } catch (error) {
      message.error({ content: "Error al subir el comprobante", key: "uploading" });
    } finally {
      setUploadingId(null);
    }
  };

  const loadFileUrl = async (path: string) => {
    if (downloadingUrl[path]) return; // already loaded
    const url = await downloadFile(path, path.endsWith('.pdf') ? "pdf" : "image");
    setDownloadingUrl(prev => ({ ...prev, [path]: url || "" }));
  };

  const showConfirmApprove = (id: number) => {
    confirm({
      title: '¿Estás seguro de que deseas aprobar este pago?',
      icon: <ExclamationCircleOutlined />,
      content: 'Esto reactivará la suscripción si estaba inactiva.',
      onOk() {
        handleApprove(id);
      },
      onCancel() {},
    });
  };

  const columns: TableColumnProps<IPayment>[] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
    },
    ...(!isEmpresa ? [{
      title: "Empresa",
      key: "empresa",
      render: (_: any, record: IPayment) => record.edges?.user?.name || "Desconocido",
    }] : []),
    {
      title: "Monto",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => `$ ${amount?.toFixed(2)}`,
    },
    {
      title: "Fecha Vencimiento",
      dataIndex: "dueDate",
      key: "dueDate",
      render: (date: string) => {
        if (!date) return "N/A";
        return new Date(date).toLocaleDateString();
      },
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={PAYMENT_STATUS_COLOR[status] || "default"}>
          {PAYMENT_STATUS[status] || status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Comprobante",
      key: "receiptId",
      render: (_: any, record: IPayment) => {
        if (!record.receiptId) return <span style={{ color: "#8c8c8c" }}>Sin subir</span>;
        const url = downloadingUrl[record.receiptId];
        return (
          <div>
            {!url ? (
              <Button type="link" onClick={() => loadFileUrl(record.receiptId!)}>
                Cargar Recibo
              </Button>
            ) : (
              <a href={url} target="_blank" rel="noopener noreferrer">
                Ver Recibo
              </a>
            )}
          </div>
        );
      },
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_: any, record: IPayment) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Upload
            beforeUpload={(file) => {
              handleUploadReceipt(file, record.id);
              return false; // Prevent default upload behavior
            }}
            showUploadList={false}
            accept=".pdf,image/*"
          >
            <Button icon={<UploadOutlined />} loading={uploadingId === record.id}>
              {record.receiptId ? "Actualizar" : "Subir"}
            </Button>
          </Upload>

          {/* Admin controls */}
          {!isEmpresa && record.receiptId && (
            <>
              <Button 
                type="primary" 
                onClick={() => showConfirmApprove(record.id)}
                disabled={record.status === "paid" || record.status === "failed"}
              >
                Aprobar
              </Button>
              <Button 
                danger 
                onClick={() => handleReject(record.id)}
                disabled={record.status === "paid" || record.status === "failed"}
              >
                Rechazar
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="payments-page" style={{ padding: 24, width: '100%' }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ marginBottom: 4, fontWeight: 700, fontSize: 24 }}>Gestión de Pagos</h2>
        <span style={{ color: '#8c8c8c' }}>{isEmpresa ? "Sube tus comprobantes aquí para regularizar tu suscripción." : "Valida o rechaza los comprobantes de depósito emitidos por clientes."}</span>
      </div>
      <Card>
        <div style={{ marginBottom: 16, display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "center" }}>
          {!isEmpresa && (
            <Input.Search
              placeholder="Buscar por Empresa..."
              onSearch={val => setSearchTerm(val)}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
          )}
          <Select
            defaultValue="all"
            style={{ width: 150 }}
            onChange={val => setStatusFilter(val)}
            options={[
              { value: 'all', label: 'Todos los estados' },
              { value: 'pending', label: 'Pendientes' },
              { value: 'processing', label: 'Procesando' },
              { value: 'paid', label: 'Pagados' },
              { value: 'failed', label: 'Fallidos' },
              { value: 'cancelled', label: 'Cancelados' },
            ]}
          />
          <DatePicker.RangePicker
            onChange={(_, dateStrings) => {
              if (!dateStrings[0]) setDateRange(null);
              else setDateRange([dateStrings[0], dateStrings[1]]);
            }}
          />
          <Button onClick={refetch}>Recargar Datos</Button>
        </div>
        <Table<IPayment>
          columns={columns}
          dataSource={data}
          rowKey="id"
          pagination={{
            position: ["bottomRight"],
            current: tableParams.pagination.current,
            pageSize: tableParams.pagination.pageSize,
            total,
            showSizeChanger: true,
          }}
          loading={loading}
          onChange={onTableChange}
        />
      </Card>
    </div>
  );
};

export default Payments;
